import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLayout } from 'hooks/useLayout'
import { Modal } from 'components/Modal/Modal'
import { type StateFileUpload } from 'types/fileUpload'
import { Box, Button } from '@mui/material'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { FormFieldErrorMessage } from 'components/FormFieldErrorMessage'
import { UploaderDisplayer } from 'components/FileUploader/UploaderDisplayer'
import { ActivityButton } from 'components/ActivityButton'

export const ImportsLocations = ({
  isModalOpen,
  toggleModalIsOpen,
  onImportLocations,
}: {
  isModalOpen: boolean
  toggleModalIsOpen: () => void
  onImportLocations: (locationIds: number[]) => void
}) => {
  const { isMobile } = useLayout()
  const { handleSubmit } = useForm()
  const [stateFilesUpload, setStateFilesUpload] = useState<StateFileUpload>(0)
  const [messageError, setMessageError] = useState('')
  const [showFiles, setShowFiles] = useState(false)
  const [files, setFiles] = useState<File[]>([])
  const toggleShowFiles = () => setShowFiles(!showFiles)

  const addFiles = (files: File[]) => {
    setFiles(files)
    toggleShowFiles()
  }

  const removeFile = (file: File) => {
    setFiles(
      files?.filter((fileMatch) => {
        return fileMatch.name !== file.name
      })
    )
    setStateFilesUpload(0)
    toggleShowFiles()
  }

  const toggleStateFilesUpload = (value: StateFileUpload) => {
    setMessageError('')
    if (stateFilesUpload === 0) {
      setStateFilesUpload(value)
    }
    if (value === 2) {
      setMessageError('Please correct errors to continue')
    }
  }

  const resetAll = () => {
    toggleModalIsOpen()
    setShowFiles(false)
    setFiles([])
    setMessageError('')
  }

  const updateLocationsWrapper = handleSubmit(async () => {
    if (stateFilesUpload !== 1) {
      setMessageError('Please correct errors to continue')
      return
    }

    const importFile = files[0]

    if (!importFile) {
      setMessageError('Please select a CSV file to continue')
      return
    }

    const text = await importFile.text()
    const locationIds = text
      .split(/\r?\n/)
      .map((row) => row.split(',')[0]?.trim())
      .filter((value): value is string => Boolean(value))
      .map(Number)
      .filter((value) => !Number.isNaN(value))

    onImportLocations(locationIds)

    resetAll()
  })

  return (
    <Modal
      isOpen={isModalOpen}
      toggleIsOpen={() => {}}
      sx={{
        width: isMobile ? '100%' : '60%',
        height: isMobile ? '100%' : '75%',
      }}
    >
      <Box>
        <form onSubmit={updateLocationsWrapper}>
          <ModalFullHeader
            toggleIsOpen={toggleModalIsOpen}
            title="Import Locations"
          />
          <ModalFullBody heightModal={isMobile ? '100%' : '75%'}>
            {messageError && (
              <Box className="w-full mb-[3%]">
                <FormFieldErrorMessage
                  message={messageError}
                  variant="contained"
                />
              </Box>
            )}
            <UploaderDisplayer
              files={files}
              addFiles={addFiles}
              removeFile={removeFile}
              toggleStateFilesUpload={toggleStateFilesUpload}
              showImages={showFiles}
              textToShowButton="Click to import."
              informationFileUpload="Select a CSV file. The first column must contain the Licensed Establishment ID"
              maxSizeAllowedUpload={5000}
              fileFormatsAllowedUpload=".csv"
            />
          </ModalFullBody>
          <ModalFullFooter>
            <Button onClick={resetAll}>Cancel</Button>
            <ActivityButton type="submit" variant="contained" active={false}>
              Import
            </ActivityButton>
          </ModalFullFooter>
        </form>
      </Box>
    </Modal>
  )
}
